import { AppLink } from '@/components/ui/AppLink';
import { Button } from '@/components/ui/Button';
import { useConsent } from '@/lib/consent/store';
import { useT } from '@/lib/i18n';

/**
 * The first-visit card (D-372, D-373): a small card at the foot of the page that says, in a few words, that the site uses
 * cookies and that pressing a button accepts the Legal Disclaimer — with a link that opens the whole disclaimer on its own
 * page. Both buttons accept the disclaimer; they differ only in the cookie choice (all, or the essential ones only), and
 * they are the same component with the same style and size — equal weight, no dark pattern. The acceptance is saved in
 * the database and on the device (`lib/consent/store.ts`, `lib/legal/record.ts`).
 *
 * Renders nothing on the server and on the first client render: it only appears after the stored choice has been read,
 * so returning visitors never see it flash.
 */
export function ConsentBar() {
  const t = useT();
  const { decision, hydrated, accept, reject } = useConsent();

  if (!hydrated || decision !== 'unset') return null;

  const [before = '', after = ''] = t('consent.body').split('{link}');
  return (
    <section className="consent-bar" aria-label={t('consent.region')}>
      <p className="consent-bar__title">{t('consent.title')}</p>
      <p className="consent-bar__body">
        {before}
        <AppLink to="/legal-disclaimer" className="consent-bar__link">
          {t('consent.link')}
        </AppLink>
        {after}
      </p>
      <div className="consent-bar__actions">
        <Button variant="glass" size="sm" onClick={accept}>
          {t('consent.accept')}
        </Button>
        <Button variant="glass" size="sm" onClick={reject}>
          {t('consent.acceptEssential')}
        </Button>
      </div>
    </section>
  );
}
